import { format } from 'date-fns';

/**
 * Когда выбор в календаре периода считается законченным и что отдавать наружу.
 *
 * `react-date-range` зовёт `onChange` на каждом клике: первый даёт диапазон из одного дня
 * (`startDate = endDate = день`), второй — весь. Раньше `DateRangePicker` отдавал наружу оба,
 * и период из одного дня с первого клика уезжал во все графики раздела. Теперь наружу уходит
 * только законченный выбор.
 *
 * Признак законченности — фокус, который библиотека передаёт в `onRangeFocusChange` сразу после
 * `onChange` того же клика: `[диапазон, 1]` — ждём конец, `[диапазон, 0]` — выбор закончен
 * (второй клик или выделение протяжкой, которая отдаёт сразу оба конца).
 */
export const isRangeSelectionComplete = (focusedRange) =>
  Array.isArray(focusedRange) && focusedRange[1] === 0;

export const selectionToPeriod = (selection) =>
  selection?.startDate && selection?.endDate
    ? { from: selection.startDate, to: selection.endDate }
    : null;

const toDay = (value) => (value ? format(value, 'yyyy-MM-dd') : null);

/**
 * Периоды сравниваются по календарным дням: разделы шлют на сервер `yyyy-MM-dd`, и период
 * с тем же днём, но другим временем суток данные не меняет.
 */
export const isSamePeriod = (a, b) =>
  toDay(a?.from) === toDay(b?.from) && toDay(a?.to) === toDay(b?.to);

/**
 * Период из черновика, который нужно отдать наружу, или `null`. Тот же период повторно не
 * отдаётся: новый объект `Date` перезапросил бы все графики раздела впустую.
 */
export const periodToCommit = (selection, value) => {
  const period = selectionToPeriod(selection);
  if (!period || isSamePeriod(period, value)) {
    return null;
  }

  return period;
};

/**
 * Кнопка очистки. Календарь, владелец которого знает свой период по умолчанию
 * (`getDefaultValue`), без периода не остаётся: кнопка возвращает к умолчанию и называется
 * «Сбросить».
 *
 * Раньше общий календарь по «Очистить» отдавал наружу «период не выбран», и разделы
 * расходились: KPI и «Общая статистика» подставляли текущий месяц, остальные графики
 * не перезапрашивались и показывали прежний период, журнал звонков снимал фильтр по дате
 * совсем, а кнопка писала «Выберите период».
 *
 * Без умолчания (период пересчёта в «Узлах анализа») — прежнее «Очистить»: там «не выбран» —
 * законное состояние, кнопка запуска без периода недоступна.
 */
export const clearButtonLabel = (getDefaultValue) => (getDefaultValue ? 'Сбросить' : 'Очистить');

/** Период после очистки и нужно ли отдавать его наружу (тот же период — не нужно). */
export const resolveClear = (getDefaultValue, value) => {
  const period = getDefaultValue ? getDefaultValue() : undefined;
  return { period, changed: !period || !isSamePeriod(period, value) };
};
